<script setup>
// An uploaded liability waiver, readable on the signup form itself.
//
// A waiver is AGREED TO on the page, so the attendee has to be able to
// read it there: a download link would be a box ticked over a document
// nobody opened. So the file is drawn into a fixed-height box that
// scrolls inside itself — tall enough to read, short enough that the
// form under it is still on the screen.
//
// A PDF is drawn page by page with pdf.js rather than put in an
// <iframe>, because an iframe is not a reader on a phone: Android
// Chrome shows no PDF in one at all and iOS shows the first page, and
// a phone is where most people sign up. pdf.js is imported only when
// a waiver is a PDF, so no other page pays for it. The LEGACY build,
// because the modern one needs browser features a two-year-old iPhone
// does not have, and this is a public form.
//
// One component for every signup form — the website's two designs and
// the report app — so they cannot disagree about how a waiver reads.
import { onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
    // { url, kind: 'document' | 'image', extension, title }
    file: { type: Object, required: true },
    // The window's fixed height. The document scrolls inside it.
    height: { type: String, default: '300px' },
});

const pages = ref(null);
const status = ref(props.file.kind === 'image' ? 'ready' : 'loading');
const isPdf = props.file.extension === 'pdf';
const isImage = props.file.kind === 'image';
// A very long file is still readable, just not all drawn: past this
// the open-in-a-new-tab link under the box is the honest way in.
const MAX_PAGES = 40;
const truncated = ref(false);
let doc = null;
let cancelled = false;

onMounted(async () => {
    if (isImage) return;
    if (!isPdf) {
        status.value = 'error';
        return;
    }
    try {
        const [pdfjs, worker] = await Promise.all([
            import('pdfjs-dist/legacy/build/pdf.mjs'),
            import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
        ]);
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
        // Fetched here and handed over as bytes: one plain GET, which
        // the file route answers cross-origin for a customer's own
        // domain, rather than pdf.js's range requests.
        const res = await fetch(props.file.url, { credentials: 'omit' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = new Uint8Array(await res.arrayBuffer());
        if (cancelled) return;
        doc = await pdfjs.getDocument({ data, isEvalSupported: false }).promise;
        if (cancelled) return;
        const width = Math.max(200, (pages.value?.clientWidth || 300));
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const count = Math.min(doc.numPages, MAX_PAGES);
        truncated.value = doc.numPages > MAX_PAGES;
        for (let n = 1; n <= count; n++) {
            const page = await doc.getPage(n);
            if (cancelled) return;
            const scale = width / page.getViewport({ scale: 1 }).width;
            const viewport = page.getViewport({ scale: scale * dpr });
            const canvas = document.createElement('canvas');
            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);
            canvas.className = 'tiq-wd__page';
            canvas.setAttribute('aria-hidden', 'true');
            pages.value.appendChild(canvas);
            await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
            // The first page drawn is the waiver showing; the rest
            // arrive below it while the reader starts at the top.
            if (n === 1) status.value = 'ready';
        }
        if (count === 0) status.value = 'error';
    } catch (e) {
        if (!cancelled) {
            console.warn('[waiver] could not draw the waiver file:', e);
            status.value = 'error';
        }
    }
});

onBeforeUnmount(() => {
    cancelled = true;
    doc?.destroy?.();
});
</script>

<template>
    <div class="tiq-wd" data-waiver-document>
        <div class="tiq-wd__box" :style="{ height }" tabindex="0"
             role="document" :aria-label="`Waiver: ${file.title || 'document'}`">
            <img v-if="isImage" :src="file.url" :alt="`Waiver: ${file.title || ''}`" class="tiq-wd__page" @error="status = 'error'">
            <div v-else ref="pages" class="tiq-wd__pages"></div>
            <p v-if="status === 'loading'" class="tiq-wd__note">Loading the waiver…</p>
            <p v-else-if="status === 'error'" class="tiq-wd__note">
                The waiver could not be shown here. Open it with the link below to read it.
            </p>
        </div>
        <p class="tiq-wd__foot">
            <span v-if="truncated">The first {{ MAX_PAGES }} pages are shown. </span>
            <a :href="file.url" target="_blank" rel="noopener" class="tiq-wd__open" data-waiver-open>Open in a new tab</a>
        </p>
    </div>
</template>

<style scoped>
.tiq-wd { display: flex; flex-direction: column; gap: 6px; }
.tiq-wd__box {
    position: relative;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding: 8px;
    background: #ecebe7;
    border: 1px solid rgba(16, 26, 21, 0.12);
    border-radius: 6px;
}
.tiq-wd__pages { display: flex; flex-direction: column; gap: 8px; }
.tiq-wd__page,
.tiq-wd__pages :deep(.tiq-wd__page) {
    display: block;
    width: 100%;
    height: auto;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}
.tiq-wd__note {
    margin: 0;
    padding: 24px 12px;
    text-align: center;
    font-size: 13px;
    line-height: 1.5;
    color: #4b5563;
}
.tiq-wd__foot { margin: 0; font-size: 12.5px; color: #4b5563; }
.tiq-wd__open { color: inherit; font-weight: 600; text-decoration: underline; }
</style>
